import type { IProgram } from '../types.js';

/** Refugee claim in Canada: Basis of Claim, then the Refugee Protection Division hearing. Restricted. */
export const REFUGEE_CLAIM: IProgram = {
  id: 'refugee-claim',
  name: 'Refugee claim in Canada (Basis of Claim and RPD hearing)',
  short: 'Refugee claim',
  family: 'refugee',
  status: 'active',
  restricted: true,
  version: '2026-10-04',
  review: 'pending',
  sources: [
    { title: 'IRCC: Claim refugee protection from inside Canada, how to apply', url: 'https://www.canada.ca/en/immigration-refugees-citizenship/services/refugees/claim-protection-inside-canada/apply.html', retrieved: '2026-10-04' },
    { title: 'IRB: Refugee claims', url: 'https://irb.gc.ca/en/refugee-claims/Pages/index.aspx', retrieved: '2026-10-04' }
  ],
  questions: [
    { id: 'portEntry', text: 'Was the claim made at a port of entry (rather than online from inside Canada)?', kind: 'yesno' }
  ],
  roles: [
    { id: 'principal', label: 'Claimant', min: 1, max: 1 },
    { id: 'dependant', label: 'Family member claiming with them', min: 0, max: 15 }
  ],
  sections: ['Forms', 'Evidence', 'Identity and civil documents', 'Hearing'],
  items: [
    { id: 'boc', label: 'Basis of Claim form, with the narrative', section: 'Forms', forRoles: ['principal', 'dependant'], source: 1, hint: 'Due within 15 days of the referral to the RPD when the claim was made at a port of entry; online claims submit it with the claim.' },
    { id: 'counsel', label: 'Counsel contact information form (IRB)', section: 'Forms', forRoles: 'application', source: 1 },
    { id: 'identity', label: 'Identity documents (passport, national ID, birth certificate)', section: 'Identity and civil documents', forRoles: ['principal', 'dependant'], source: 0, translationIfNotEnFr: true },
    { id: 'personalEvidence', label: 'Personal evidence supporting the claim (reports, letters, medical or police records)', section: 'Evidence', forRoles: 'application', source: 1, translationIfNotEnFr: true },
    { id: 'countryEvidence', label: 'Country condition evidence (National Documentation Package and other sources)', section: 'Evidence', forRoles: 'application', source: 1 },
    { id: 'priorApplications', label: 'Earlier visa, study or other applications, checked for consistency with the Basis of Claim', section: 'Evidence', forRoles: 'application', source: 1 },
    { id: 'disclosure', label: 'Documents disclosed to the RPD (at least 10 days before the hearing)', section: 'Hearing', forRoles: 'application', source: 1 },
    { id: 'preparation', label: 'Hearing preparation with the claimant (questions, mock hearing)', section: 'Hearing', forRoles: ['principal'], source: 1 }
  ],
  deadlines: [
    { id: 'boc', event: 'rpdReferral', label: 'Send the Basis of Claim form to the RPD', days: 15, citation: 'IRB: Basis of Claim within 15 days of the referral (claims made at a port of entry)', caveat: 'Check the date of referral on the documents; online claims file it with the claim.', review: 'pending' },
    { id: 'disclosure', event: 'hearingDate', label: 'Disclose documents and changes to the RPD', days: -10, citation: 'IRB: documents and changes at least 10 days before the hearing', caveat: 'Counted back from the hearing date.', review: 'pending' }
  ]
};

/** Appeal of a refused refugee claim to the Refugee Appeal Division. Restricted. */
export const RAD_APPEAL: IProgram = {
  id: 'rad-appeal',
  name: 'Refugee Appeal Division (RAD) appeal',
  short: 'RAD appeal',
  family: 'refugee',
  status: 'active',
  restricted: true,
  version: '2026-10-04',
  review: 'pending',
  sources: [
    { title: 'IRB: Refugee appeals', url: 'https://irb.gc.ca/en/refugee-appeals/Pages/index.aspx', retrieved: '2026-10-04' },
    { title: 'IRB: Practice notice extending the time limit for perfecting an appeal', url: 'https://irb-cisr.gc.ca/en/legal-policy/procedures/Pages/practice-notice-extending-time-perfecting-appeal.aspx', retrieved: '2026-10-04' },
    { title: 'IRB: Preparing and submitting the appellant\'s record', url: 'https://irb-cisr.gc.ca/en/filing-refugee-appeal/filing-a-refugee-appeal/Pages/preparing-submitting-appellant-record.aspx', retrieved: '2026-10-06' }
  ],
  questions: [{ id: 'newEvidence', text: 'Will new evidence be filed?', kind: 'yesno' }],
  roles: [{ id: 'principal', label: 'Appellant', min: 1, max: 1 }, { id: 'dependant', label: 'Family member appealing with them', min: 0, max: 15 }],
  sections: ['Forms', 'Appeal record', 'Evidence'],
  items: [
    { id: 'notice', label: 'Notice of appeal', section: 'Forms', forRoles: 'application', source: 0 },
    { id: 'reasons', label: 'RPD decision and written reasons', section: 'Appeal record', forRoles: 'application', source: 0 },
    { id: 'memorandum', label: 'Appellant\'s memorandum', section: 'Appeal record', forRoles: 'application', source: 0 },
    { id: 'transcript', label: 'Transcript or recording of the RPD hearing (if relied on)', section: 'Appeal record', forRoles: 'application', source: 0 },
    { id: 'newEvidence', label: 'New evidence, with why it couldn\'t be given before', section: 'Evidence', forRoles: 'application', when: { q: 'newEvidence', is: true }, source: 0, translationIfNotEnFr: true }
  ],
  deadlines: [
    { id: 'radNotice', event: 'radReasons', label: 'File the notice of appeal (RAD)', days: 15, citation: 'Immigration and Refugee Protection Regulations s.159.91(1)(a): notice of appeal within 15 days of receiving the written reasons', review: 'pending' },
    {
      id: 'radPerfect', event: 'radReasons', label: 'Perfect the appeal (appellant\'s record, RAD)', days: 45,
      citation: 'IRB practice notice (2020), in force until further notice: appeal perfected within 45 days of receiving the written reasons (the IRB\'s appellant\'s record page says 45, checked 2026-10-06)',
      alt: { tick: 'Use 30 days: the IRB practice notice has ended', days: 30, citation: 'Immigration and Refugee Protection Regulations s.159.91(1)(b): 30 days of receiving the written reasons', caveat: 'If the IRB withdraws its practice notice, the regulations\' 30 days apply again.' },
      review: 'pending'
    }
  ]
};

/** Pre-removal risk assessment. Restricted. */
export const PRRA: IProgram = {
  id: 'prra',
  name: 'Pre-removal risk assessment (PRRA)',
  short: 'PRRA',
  family: 'refugee',
  status: 'active',
  restricted: true,
  version: '2026-10-04',
  review: 'pending',
  sources: [{ title: 'IRCC: Pre-removal risk assessment', url: 'https://www.canada.ca/en/immigration-refugees-citizenship/services/refugees/protection/refusal-options/pre-removal-risk-assessment.html', retrieved: '2026-10-04' }],
  questions: [],
  roles: [{ id: 'principal', label: 'Applicant', min: 1, max: 1 }, { id: 'dependant', label: 'Family member', min: 0, max: 15 }],
  sections: ['Forms', 'Evidence'],
  items: [
    { id: 'notification', label: 'PRRA notification from CBSA/IRCC', section: 'Forms', forRoles: 'application', source: 0 },
    { id: 'application', label: 'PRRA application form', section: 'Forms', forRoles: ['principal', 'dependant'], source: 0 },
    { id: 'submissions', label: 'Written submissions on the risk', section: 'Evidence', forRoles: 'application', source: 0 },
    { id: 'evidence', label: 'Evidence of new risk (and country conditions)', section: 'Evidence', forRoles: 'application', source: 0, translationIfNotEnFr: true }
  ],
  deadlines: [
    { id: 'prraApply', event: 'prraNotice', label: 'Send the PRRA application', days: 15, citation: 'IRCC: within 15 days of the PRRA notification (keeps the stay of removal)', caveat: 'Counted from the notification; check the notice.', review: 'pending' },
    { id: 'prraSubmissions', event: 'prraNotice', label: 'Send the written submissions and evidence', days: 30, citation: 'IRCC: written submissions within 30 days of the notification', caveat: 'Counted from the notification; check the notice.', review: 'pending' }
  ]
};

/** Private sponsorship of refugees by a Group of Five (new intake paused to December 31, 2026). Restricted. */
export const GROUP_OF_FIVE: IProgram = {
  id: 'group-of-five',
  name: 'Private sponsorship of refugees: Group of Five',
  short: 'Group of Five',
  family: 'refugee',
  status: 'paused',
  pausedNote: 'IRCC paused new Group of Five and community sponsor applications until December 31, 2026. Applications sent before the pause are still processed; the Blended Visa Office-Referred program is open.',
  restricted: true,
  version: '2026-10-04',
  review: 'pending',
  sources: [{ title: 'IRCC: Groups of Five', url: 'https://www.canada.ca/en/immigration-refugees-citizenship/services/refugees/help-outside-canada/private-sponsorship-program/groups-five.html', retrieved: '2026-10-04' }],
  questions: [],
  roles: [
    { id: 'sponsor', label: 'Group member (sponsor)', min: 5, max: 10 },
    { id: 'principal', label: 'Refugee (principal applicant)', min: 1, max: 1 },
    { id: 'dependant', label: 'Refugee\'s family member', min: 0, max: 15 }
  ],
  sections: ['Sponsorship', 'Refugee', 'Settlement plan'],
  items: [
    { id: 'undertaking', label: 'Sponsorship undertaking and settlement plan', section: 'Sponsorship', forRoles: 'application', source: 0 },
    { id: 'financial', label: 'Financial profile of each group member', section: 'Sponsorship', forRoles: ['sponsor'], source: 0 },
    { id: 'status', label: 'Proof of each member\'s status in Canada', section: 'Sponsorship', forRoles: ['sponsor'], source: 0 },
    { id: 'recognition', label: 'Proof of the refugee\'s recognition by UNHCR or a state', section: 'Refugee', forRoles: ['principal'], source: 0 },
    { id: 'paApplication', label: 'The refugee\'s application for permanent residence and forms', section: 'Refugee', forRoles: ['principal', 'dependant'], source: 0 },
    { id: 'plan', label: 'Settlement plan details (housing, income support for a year)', section: 'Settlement plan', forRoles: 'application', source: 0 }
  ],
  deadlines: []
};

/** Blended Visa Office-Referred program (open to groups during the Group of Five pause). Restricted. */
export const BVOR: IProgram = {
  ...GROUP_OF_FIVE,
  id: 'bvor',
  name: 'Private sponsorship of refugees: Blended Visa Office-Referred (BVOR)',
  short: 'BVOR sponsorship',
  status: 'active',
  pausedNote: undefined,
  sources: [{ title: 'IRCC: Blended Visa Office-Referred program', url: 'https://www.canada.ca/en/immigration-refugees-citizenship/services/refugees/help-outside-canada/private-sponsorship-program/blended-visa-office-program.html', retrieved: '2026-10-04' }],
  roles: [
    { id: 'sponsor', label: 'Sponsor (group member)', min: 1, max: 10 },
    { id: 'principal', label: 'Refugee referred by the visa office', min: 1, max: 1 },
    { id: 'dependant', label: 'Refugee\'s family member', min: 0, max: 15 }
  ],
  items: GROUP_OF_FIVE.items.filter(i => i.id !== 'recognition')
};
